import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { appStore } from '../../app/AppStore';
import { ExtractTasksGrid } from '../../app/content/ExtractTasksGrid';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { ServerUrls } from '../../app/ServerUrls';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { FeatureFlags } from '../FeatureFlags';
import { ListResult } from '../fetchers/ListResult';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { ActionContext, IActionContext } from '../helpers/ActionContext';
import { SchedulingErrorCode } from '../helpers/ErrorCodes';
import { RefreshPropertyHelper } from '../helpers/RefreshPropertyHelper';
import { SubplaceCountsHelper, SubplaceEvents } from '../helpers/SubplaceCountsHelper';
import { ExtractTaskContentType } from '../models/ContentTypes';
import { IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { IExtractTaskInfo } from '../models/IExtractTaskInfo';
import { ISubplace } from '../models/IPlace';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { ResourceQuery } from '../resources/ResourceQuery';
import { ExtractTaskType } from '../ServerApi/types/IExtractTask';
import { IResult } from '../ServerApi/types/IResponse';
import { ActionHelpers } from '../services/ActionHelpers';
import { IActionButton, IListActionButton } from '../services/ActionPanelService';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ChangeDatasourceRemoteRefreshPropertiesAction } from '../services/ChangeDatasourceRemoteRefreshPropertiesAction';
import { ChangeExtractTaskPriorityAction } from '../services/ChangeExtractTaskPriorityAction';
import { ChangeScheduleAction } from '../services/ChangeScheduleAction';
import { DeleteExtractTasksAction } from '../services/DeleteExtractTasksAction';
import { HelpService } from '../services/HelpService';
import { PublishDatasourceAction } from '../services/PublishDatasourceAction';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { ResumeExtractTasksAction } from '../services/ResumeExtractTasksAction';
import { RunExtractTasksAction } from '../services/RunExtractTasksAction';
import { ServerService } from '../services/ServerService';
import { SorterFactory } from '../services/SorterFactory';
import { Location } from './../../common/react/Location';
import { ListActionsScope, SubplaceWithDataGridScope } from './ContentSubplaceCtrl';
import { IDatasourcePublishResultScope } from './DatasourcePublishResultCtrl';
import { ITasksScope } from './TasksCtrl';

export interface IRemoteRefreshPropertiesButtonScope {
  currentRefreshLocation: () => string;
  refreshPropertiesAreEditable: () => boolean;
  changeRemoteRefreshProperties: ($event: JQueryEventObject) => any;
}

export interface IExtractTasksScope
extends ITasksScope,
        IActionsMenuScope<IExtractTaskInfo, {}>,
        ListActionsScope,
        SubplaceWithActionButtonsScope<IExtractTaskInfo, {}>,
        SubplaceWithDataGridScope<IExtractTaskInfo>,
        IRemoteRefreshPropertiesButtonScope,
        IDatasourcePublishResultScope {
  extractTypeTranslationKey(extractTask: IExtractTaskInfo): string;
  showRefreshProperties: () => boolean;
}

export interface IExtractTasksContext {
  createExtractTaskFn: (context: IActionContext) => ng.IPromise<any>;
  modifyExtractTasksPermission: boolean;
  datasource?: IDataSourceDetailInfo;
}


export class ExtractTasksCtrl {

  public static $inject = [
    '$q',
    '$translate',
    'AngularToReactDataGridBridge',
    '$filter',
    '$scope',
    '$state',
    'BrowserTitleService',
    'subplace',
    'extractTasksContext',
    'QueryService',
    'SorterFactory',
    'DeleteExtractTasksAction',
    'RunExtractTasksAction',
    'ResumeExtractTasksAction',
    'ChangeExtractTaskPriorityAction',
    'ChangeScheduleAction',
    'ChangeDatasourceRemoteRefreshPropertiesAction',
    'ServerService',
    'HelpService',
    'PublishDatasourceAction'
  ];

  private query: IQueryUpdater;
  private waitForCountOfSchedules: ng.IDeferred<number>;

  constructor(
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    private $filter: ng.IFilterService,
    private $scope: IExtractTasksScope,
    private $state: ng.ui.IStateService,
            BrowserTitleService: BrowserTitleService,
            subplace: ISubplace<IExtractTaskInfo>,
    private extractTasksContext: IExtractTasksContext,
            queryService: QueryService,
            sorterFactory: SorterFactory,
    private DeleteExtractTasksAction: DeleteExtractTasksAction,
    private RunExtractTasksAction: RunExtractTasksAction,
    private ResumeExtractTasksAction: ResumeExtractTasksAction,
    private ChangeExtractTaskPriorityAction: ChangeExtractTaskPriorityAction,
    private ChangeScheduleAction: ChangeScheduleAction,
            ChangeDatasourceRemoteRefreshPropertiesAction: ChangeDatasourceRemoteRefreshPropertiesAction,
    private ServerService: ServerService,
    private HelpService: HelpService,
    private PublishDatasourceAction: PublishDatasourceAction
  ) {
    BrowserTitleService.setTitleWithPlaceEntityNameAndSubplaceName({
      placeEntityName: $scope.objectName,
      translatableSubplaceName: $scope.objectName ? 'refreshSchedule' : 'extractTasks'
    });

    // If a deep link was used to this sub-place after the refresh mode was changed, redirect to the correct one.
    this.redirectByExtractsAndRefreshMode(extractTasksContext.datasource);

    this.waitForCountOfSchedules = $q.defer();
    $scope.selectedItems = new Set<IExtractTaskInfo>((extractTask: IExtractTaskInfo) => extractTask.id);

    const actions = this.createActions($scope.selectedItems);
    const getActionTarget = ActionButtonsHelper.getActionTargetFn($scope, $scope.selectedItems);

    this.configureForReactDataGrid(actions, getActionTarget);

    $scope.sorter = sorterFactory.create(ExtractTaskContentType.defaultOrder);

    this.query = queryService.createQueryUpdaterFromSubplace(
      $scope,
      subplace,
      (resourceQuery: ResourceQuery<IExtractTaskInfo>) => {
        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: $scope, sliceable: resourceQuery });
        $scope.sliceable = resourceQuery;
        $scope.sorter.setOrder(resourceQuery.getOrder());
      });
    this.refreshExtractTasks();

    $scope.$on(SubplaceEvents.Refresh, () => {
      this.refreshExtractTasks();
    });

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    $scope.listActions = [{
      name: 'new-refresh-task',
      translate: 'newExtractRefresh',
      isAvailable: () => (!!this.extractTasksContext.createExtractTaskFn && this.extractTasksContext.modifyExtractTasksPermission),
      execute: ($event: JQueryEventObject) => {
        this.extractTasksContext.createExtractTaskFn(ActionContext.fromEvent($event)).then(() => this.refreshExtractTasks());
      }
    }];
    $scope.availableListAction = (listAction: IListActionButton) => listAction.isAvailable();

    $scope.anyActionsAllowed = () => this.anyActionsAllowed();

    $scope.extractTypeTranslationKey = (extractTask: IExtractTaskInfo): string => {
      switch(extractTask.type) {
        case ExtractTaskType.IncrementExtract: return 'extractTasksIncremental';
        case ExtractTaskType.RefreshExtract:   return 'extractTasksFull';
        default:                                          return null;
      }
    };

    //// Respond to grid data updates
    // React data grid
    if ($scope.dataGridProps) {
      $scope.dataGridProps.updated = (rows: DataGrid.Row[]) => $scope.$apply(() => {
        this.waitForCountOfSchedules.resolve(rows.length);
        $scope.selectedItems.updateItems(_.map(_.compact(rows), (row) => row.data as IExtractTaskInfo));
        SubplaceCountsHelper.updateCountsFromSubplace($scope, subplace, rows.length);
      });
    }

    $scope.emptyTemplateModel = this.createEmptyTemplateModel(extractTasksContext);

    $scope.showRefreshProperties = () => {
      return ServerService.isBridgeEnabled() && extractTasksContext.datasource !== undefined;
    };

    $scope.currentRefreshLocation = () => RefreshPropertyHelper.getCurrentRefreshLocation(extractTasksContext.datasource);

    $scope.refreshPropertiesAreEditable = () => {
      return $scope.showRefreshProperties() &&
        extractTasksContext.modifyExtractTasksPermission &&
        RefreshPropertyHelper.canBeRefreshedRemotely(extractTasksContext.datasource);
    };

    $scope.changeRemoteRefreshProperties = ($event: JQueryEventObject) => {
      this.waitForCountOfSchedules.promise.then((countOfSchedules: number) => {
        ChangeDatasourceRemoteRefreshPropertiesAction.execute(extractTasksContext.datasource, countOfSchedules);
      });
    };

    this.waitForCountOfSchedules.promise.then((countOfSchedules: number) => {
      this.PublishDatasourceAction.handlePublishedDatasource(countOfSchedules, $scope.publishResult, extractTasksContext.datasource);
    });
  }

  private anyActionsAllowed() {
    return this.extractTasksContext.modifyExtractTasksPermission;
  }

  private isServerPage() {
    return this.$state.includes('main.server.**');
  }

  private showWorkbookOrDatasourceColumn() {
    // this function should also be removed with FeatureFlags.stylishTasks. This ctrl is only used on the
    // site tasks page so this is actually always true.
    const placeName = this.$scope.place && this.$scope.place.name;
    return !(placeName === 'workbook' || placeName === 'datasource');
  }

  private showScheduleColumn() {
    return !this.$state.includes('**.schedule.**');
  }

  private showSiteColumn() {
    return this.isServerPage();
  }

  private showPriorityColumn() {
    return !this.ServerService.ignoreExtractTaskPriority();
  }

  private redirectByExtractsAndRefreshMode(datasource: IDataSourceDetailInfo): void {
    if (datasource) {
      // Prevent deep links for live data sources.
      if (!datasource.hasExtracts) {
        const siteUrls = siteUrlsSelector(appStore.getState());
        Location.instance.setUrl(siteUrls.datasourceConnectedWorkbooksUrl(datasource.id)); // Go to default sibling state
      }

      // Prevent deep links to the incorrect refresh schedules page
      RefreshPropertyHelper.redirectByDatasourceRefreshMode(datasource.refreshMode, datasource.id);
    }
  }

  private createActions(selectedItems: Set<IExtractTaskInfo>): IActionButton<IExtractTaskInfo, {}>[] {
    return [
      {
        name: 'tasks-run-now',
        translate: 'runNow_action',
        isAvailable: (extractTasks: Set<IExtractTaskInfo>) =>
          !extractTasks.isEmpty() && this.extractTasksContext.modifyExtractTasksPermission,
        execute: (items: Set<IExtractTaskInfo>, context: IActionContext) => {
          this.RunExtractTasksAction.execute(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              this.refreshExtractTasks();
            });
        }
      },
      {
        name: 'tasks-resume-extract-refresh',
        translate: 'resumeExtractRefresh_action',
        isAvailable: (extractTasks: Set<IExtractTaskInfo>) =>
          FeatureFlags.resumePausedExtractRefresh && !extractTasks.isEmpty() && this.extractTasksContext.modifyExtractTasksPermission,
          isDisabled: (extractTasks: Set<IExtractTaskInfo>) => extractTasks.all(task => task.state === 'Active'),
          execute: (items: Set<IExtractTaskInfo>, context: IActionContext) => {
          this.ResumeExtractTasksAction.execute(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              this.refreshExtractTasks();
            });
        }
      },
      {
        name: 'tasks-change-schedule',
        translate: 'changeSchedule_action',
        isAvailable: (extractTasks: Set<IExtractTaskInfo>) =>
          !extractTasks.isEmpty() && this.extractTasksContext.modifyExtractTasksPermission,
        execute: (items: Set<IExtractTaskInfo>, context: IActionContext) => {
          this.ChangeScheduleAction.forExtractTasks(items, context)
            .then((result: IResult) => {
              const scheduleScope = <any>this.$scope;
              if (scheduleScope.schedule) {
                ActionHelpers.deselectAllExceptErrors(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              } else {
                ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              }
              this.refreshExtractTasks();
            });
        }
      },
      {
        name: 'tasks-priority',
        translate: 'tasksSetPriority_action',
        isAvailable: (extractTasks: Set<IExtractTaskInfo>) =>
          !this.ServerService.ignoreExtractTaskPriority() && !extractTasks.isEmpty() && this.ServerService.isSessionSiteOrServerAdmin(),
        execute: (items: Set<IExtractTaskInfo>, context: IActionContext) => {
          this.ChangeExtractTaskPriorityAction.execute(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              this.refreshExtractTasks();
            });
        }
      },
      {
        name: 'tasks-delete',
        translate: 'delete_action',
        isAvailable: (extractTasks: Set<IExtractTaskInfo>) =>
          !extractTasks.isEmpty() && this.extractTasksContext.modifyExtractTasksPermission,
        execute: (items: Set<IExtractTaskInfo>, context: IActionContext) => {
          this.DeleteExtractTasksAction.executeWithContext(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectAllExceptErrors(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              this.refreshExtractTasks();
            });
        }
      }
    ];
  }

  private refreshExtractTasks() {
    this.waitForCountOfSchedules = this.$q.defer();
    this.query.refresh();
  }

  private createEmptyTemplateModel(extractTasksContext: IExtractTasksContext): IEmptyTemplateModel {
    const emptyTemplateModel = <IEmptyTemplateModel> {};
    emptyTemplateModel.title = 'extractTasksEmptyMessage';

    // The extract tasks grid is used in multiple locations, only show this information for data sources.
    // Only data sources have a refresh mode, check that it exists.
    if(this.ServerService.isBridgeEnabled() && extractTasksContext.datasource) {
      emptyTemplateModel.help = {
        links: [{
          localizedValueKey: 'remoteRefreshSchedulesEmptyHelp',
          url: this.HelpService.getDatasourceHelpUrl()
        }]
      };

      // Don't display the help information for OAuth connections because they can only be refreshed on server
      if(extractTasksContext.datasource && !extractTasksContext.datasource.connectionOAuth) {
        emptyTemplateModel.body = [{
          statements: [
            {
              localizedStatementKey: 'remoteRefreshSchedulesEmptyParagraph1'
            }
          ]
        }];
      }
    }

    return emptyTemplateModel;
  }

  private configureForReactDataGrid(actions: IActionButton<IExtractTaskInfo, {}>[],
                                    getActionTarget: () => IActionTarget<IExtractTaskInfo, {}>) {

    const siteUrls = new SiteUrls({
      siteUrlName: this.ServerService.sessionSiteUrlName(),
      queryString: window.location.search
    });

    const columns = ExtractTasksGrid.columns(
      (id: string) => this.$translate.instant(id),
      this.$scope,
      this.anyActionsAllowed(),
      actions,
      getActionTarget,
      this.isServerPage(),
      ServerUrls.getInstance(),
      siteUrls,
      <IScheduleFrequencyDescriptionFilter>this.$filter('scheduleFrequencyDescription'),
      this.showWorkbookOrDatasourceColumn(),
      this.showScheduleColumn(),
      this.showSiteColumn(),
      this.showPriorityColumn(),
      this.ServerService.isSessionSiteOrServerAdmin(),
    );

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(this.createEmptyTemplateModel(this.extractTasksContext)),
      canShowFilterPanel: ExtractTaskContentType.canShowFilterPanel
    });

  }

}

Module.instance().controller('ExtractTasksCtrl', ExtractTasksCtrl);
